import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MessageCircle, Phone, Mail, Download, Plus, Users, UserCheck, Send, CheckCircle, Image, MapPin, Clock, Building, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import AdminLayout from '@/components/AdminLayout';
import { normalizePhoneForWhatsApp, isValidWhatsAppNumber, openWhatsApp } from '@/utils/phone';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusOptions = ['Neu', 'Kontaktiert', 'Angebot gesendet', 'Abgeschlossen'];
const statusColors = {
  'Neu': 'bg-blue-100 text-blue-800',
  'Kontaktiert': 'bg-yellow-100 text-yellow-800',
  'Angebot gesendet': 'bg-purple-100 text-purple-800',
  'Abgeschlossen': 'bg-green-100 text-green-800'
};

const AdminDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, neu: 0, kontaktiert: 0, angebot: 0, abgeschlossen: 0 });
  const [galleryStats, setGalleryStats] = useState({ total: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  // Lead-Löschung
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Lead-Lifecycle / Event-Timeline (Phase 1)
  const [leadEvents, setLeadEvents] = useState([]);
  const [lifecycle, setLifecycle] = useState({ quality: '', order_value: '', profit: '', closed_at: '' });
  const [savingLifecycle, setSavingLifecycle] = useState(false);

  const authHeader = {}; // CRP-011: Cookie-Auth — kein Bearer-Token mehr nötig

  const fetchData = useCallback(async () => {
    try {
      const [leadsRes, statsRes] = await Promise.all([
        api.get('/leads', authHeader),
        api.get('/stats', authHeader)
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      // Gallery stats - optional, ignore error
      try {
        const g = await api.get('/admin/gallery/stats', authHeader);
        setGalleryStats(g.data);
      } catch (e) {
        // Gallery stats sind optional - ein veralteter Deploy ohne Gallery-Endpoint soll
        // das Dashboard nicht blockieren. Wir loggen es nur fuer DevTools.
        console.warn('[Dashboard] gallery stats unavailable:', e?.response?.status, e?.message);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        window.location.href = '/admin/login';
      }
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Sync Lifecycle-Felder + Event-Timeline beim Öffnen eines Leads
  useEffect(() => {
    if (!selectedLead) {
      setLeadEvents([]);
      setLifecycle({ quality: '', order_value: '', profit: '', closed_at: '' });
      return;
    }
    setLifecycle({
      quality: selectedLead.quality || '',
      order_value: selectedLead.order_value ?? '',
      profit: selectedLead.profit ?? '',
      closed_at: (selectedLead.closed_at || '').slice(0, 10),
    });
    let cancelled = false;
    api.get(`/leads/${selectedLead.id}/events`, authHeader)
      .then(res => { if (!cancelled) setLeadEvents(res.data?.items || []); })
      .catch(() => { if (!cancelled) setLeadEvents([]); });
    return () => { cancelled = true; };
  }, [selectedLead?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const saveLifecycle = async () => {
    if (!selectedLead) return;
    setSavingLifecycle(true);
    try {
      const body = {};
      if (lifecycle.quality) body.quality = lifecycle.quality;
      if (lifecycle.order_value !== '') body.order_value = parseFloat(lifecycle.order_value);
      if (lifecycle.profit !== '') body.profit = parseFloat(lifecycle.profit);
      if (lifecycle.closed_at) body.closed_at = lifecycle.closed_at;
      if (Object.keys(body).length === 0) {
        toast.message('Keine Änderungen');
        setSavingLifecycle(false);
        return;
      }
      await api.patch(`/leads/${selectedLead.id}`, body, authHeader);
      toast.success('Lead-Daten gespeichert');
      // Re-fetch lead to reflect server-side updates
      const fresh = await api.get(`/leads/${selectedLead.id}`, authHeader);
      setSelectedLead(fresh.data);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSavingLifecycle(false);
    }
  };

  const updateStatus = async (leadId, newStatus) => {
    try {
      await api.patch(`/leads/${leadId}/status?status=${encodeURIComponent(newStatus)}`, {}, authHeader);
      toast.success(`Status geändert: ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error('Fehler beim Status-Update');
    }
  };

  const addNote = async () => {
    if (!noteText.trim() || !selectedLead) return;
    setAddingNote(true);
    try {
      await api.post(`/leads/${selectedLead.id}/notes`, { text: noteText }, authHeader);
      toast.success('Notiz gespeichert');
      setNoteText('');
      fetchData();
      const updatedLead = await api.get(`/leads/${selectedLead.id}`, authHeader);
      setSelectedLead(updatedLead.data);
    } catch (err) {
      toast.error('Fehler beim Speichern der Notiz');
    } finally {
      setAddingNote(false);
    }
  };

  // ---- Lead Deletion ----
  const allLeadIds = useMemo(() => leads.map(l => l.id), [leads]);
  const allSelected = allLeadIds.length > 0 && allLeadIds.every(id => selectedIds.has(id));
  const someSelected = !allSelected && allLeadIds.some(id => selectedIds.has(id));

  const toggleOne = (id, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const toggleAll = (checked) => {
    setSelectedIds(checked ? new Set(allLeadIds) : new Set());
  };

  const clearSelection = () => setSelectedIds(new Set());

  const confirmSingleDelete = async () => {
    if (!deleteTargetId) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/leads/${deleteTargetId}`, authHeader);
      toast.success('Lead erfolgreich gelöscht');
      // Aufräumen
      setSelectedIds(prev => { const n = new Set(prev); n.delete(deleteTargetId); return n; });
      if (selectedLead?.id === deleteTargetId) setSelectedLead(null);
      setDeleteTargetId(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Löschen fehlgeschlagen');
    } finally {
      setDeleting(false);
    }
  };

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setDeleting(true);
    try {
      const res = await api.post('/admin/leads/bulk-delete', { lead_ids: ids }, authHeader);
      const n = res.data?.deleted_count ?? ids.length;
      toast.success(`${n} Leads erfolgreich gelöscht`);
      if (selectedLead && ids.includes(selectedLead.id)) setSelectedLead(null);
      clearSelection();
      setBulkConfirmOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk-Löschen fehlgeschlagen');
    } finally {
      setDeleting(false);
    }
  };

  const exportCSV = async () => {
    try {
      const response = await api.get('/leads/export/csv', {
        ...authHeader,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exportiert');
    } catch (err) {
      toast.error('Fehler beim CSV-Export');
    }
  };

  const formatDate = (timestamp) => {
    const d = new Date(timestamp);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="text-center py-12 text-gray-500">Wird geladen...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="dashboard-stats">
        <div className="bg-white rounded-lg p-4 border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#4A331D]">{stats.total}</p>
              <p className="text-xs text-gray-500">Gesamt</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Plus className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#4A331D]">{stats.neu}</p>
              <p className="text-xs text-gray-500">Neue Leads</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#4A331D]">{stats.kontaktiert}</p>
              <p className="text-xs text-gray-500">Kontaktiert</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#4A331D]">{stats.abgeschlossen}</p>
              <p className="text-xs text-gray-500">Abgeschlossen</p>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Quick Info */}
      <div className="bg-white border rounded-lg p-3 mb-4 flex items-center justify-between" data-testid="gallery-quick-info">
        <div className="flex items-center gap-2 text-sm">
          <span className="w-7 h-7 rounded bg-[#D4AA6A]/20 text-[#4A331D] flex items-center justify-center">📷</span>
          <span className="text-gray-700">
            <span className="font-semibold text-[#4A331D]">Galerie:</span> {galleryStats.active} aktive Bilder
            <span className="text-gray-400"> · {galleryStats.total} gesamt</span>
          </span>
        </div>
        <Button size="sm" variant="outline" onClick={() => window.location.href = '/admin/gallery'} data-testid="gallery-manage-btn">
          Verwalten
        </Button>
      </div>

      {/* Actions Bar */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-600">{leads.length} Leads insgesamt</p>
        <Button onClick={exportCSV} variant="outline" size="sm" data-testid="csv-export-btn">
          <Download className="w-4 h-4 mr-2" />
          CSV Export
        </Button>
      </div>

      {/* Bulk Delete Toolbar */}
      {selectedIds.size > 0 && (
        <div
          className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5"
          data-testid="bulk-toolbar"
        >
          <span className="text-sm font-medium text-amber-900" data-testid="bulk-selection-count">
            {selectedIds.size} Lead{selectedIds.size === 1 ? '' : 's'} ausgewählt
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={clearSelection}
              data-testid="bulk-clear-btn"
            >
              Auswahl aufheben
            </Button>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => setBulkConfirmOpen(true)}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Ausgewählte Leads löschen
            </Button>
          </div>
        </div>
      )}

      {/* Leads Table */}
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden" data-testid="leads-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-3 w-10">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={(c) => toggleAll(!!c)}
                    aria-label="Alle Leads auswählen"
                    data-testid="select-all-leads"
                    // visual mixed state hint (shadcn Checkbox is binary; we expose via testid)
                    data-indeterminate={someSelected ? 'true' : 'false'}
                  />
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Telefon</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">E-Mail</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Leistung</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Quelle</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Datum</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-8 text-gray-400">
                    Noch keine Leads vorhanden
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr
                    key={lead.id}
                    className={`border-b hover:bg-gray-50 cursor-pointer ${selectedIds.has(lead.id) ? 'bg-amber-50/40' : ''}`}
                    onClick={() => setSelectedLead(lead)}
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.has(lead.id)}
                        onCheckedChange={(c) => toggleOne(lead.id, !!c)}
                        aria-label={`Lead ${lead.name} auswählen`}
                        data-testid={`select-lead-${lead.id}`}
                      />
                    </td>
                    <td className="px-4 py-3 font-medium text-[#4A331D]">{lead.name}</td>
                    <td className="px-4 py-3 text-gray-700">{lead.phone}</td>
                    <td className="px-4 py-3 text-gray-700">{lead.email || '-'}</td>
                    <td className="px-4 py-3 text-gray-700">{lead.service || lead.project_type}</td>
                    <td className="px-4 py-3">
                      {lead.source && (
                        <span className={`px-1.5 py-0.5 rounded text-xs ${lead.source === 'Projektformular' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                          {lead.source === 'Projektformular' ? 'Projekt' : lead.source === 'Schnellformular' ? 'Schnell' : lead.source || '-'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(lead.timestamp)}</td>
                    <td className="px-4 py-3">
                      <Select
                        value={lead.status}
                        onValueChange={(val) => { updateStatus(lead.id, val); }}
                      >
                        <SelectTrigger
                          className={`h-7 text-xs w-36 ${statusColors[lead.status] || ''}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const wa = isValidWhatsAppNumber(normalizePhoneForWhatsApp(lead.phone));
                          return (
                            <button
                              onClick={() => openWhatsApp(lead.phone)}
                              disabled={!wa}
                              className={`p-1.5 rounded ${wa ? 'hover:bg-green-50 text-green-600' : 'text-gray-300 cursor-not-allowed'}`}
                              title={wa ? 'WhatsApp' : 'Keine gültige WhatsApp-Nummer vorhanden'}
                              data-testid={`lead-wa-row-${lead.id}`}
                            >
                              <MessageCircle className="w-4 h-4" />
                            </button>
                          );
                        })()}
                        <button
                          onClick={() => window.open(`tel:${lead.phone}`, '_self')}
                          className="p-1.5 hover:bg-blue-50 rounded text-blue-600"
                          title="Anrufen"
                        >
                          <Phone className="w-4 h-4" />
                        </button>
                        {lead.email && (
                          <button
                            onClick={() => window.open(`mailto:${lead.email}`, '_self')}
                            className="p-1.5 hover:bg-purple-50 rounded text-purple-600"
                            title="E-Mail"
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteTargetId(lead.id)}
                          className="p-1.5 hover:bg-red-50 rounded text-red-600"
                          title="Lead löschen"
                          data-testid={`delete-lead-${lead.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lead Detail / Notes Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4" onClick={() => setSelectedLead(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="lead-detail-modal">
            <div className="p-5 border-b">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-lg text-[#4A331D]">{selectedLead.name}</h3>
                  <p className="text-sm text-gray-500">{selectedLead.service || selectedLead.project_type}</p>
                  {selectedLead.source && (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">{selectedLead.source}</span>
                  )}
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[selectedLead.status]}`}>
                  {selectedLead.status}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">Telefon:</span> {selectedLead.phone}</div>
                <div><span className="text-gray-500">E-Mail:</span> {selectedLead.email || '-'}</div>
                {selectedLead.location && <div><span className="text-gray-500">Ort:</span> {selectedLead.location}</div>}
                <div><span className="text-gray-500">Datum:</span> {formatDate(selectedLead.timestamp)}</div>
                {selectedLead.description && (
                  <div className="col-span-2"><span className="text-gray-500">Beschreibung:</span> {selectedLead.description}</div>
                )}
                {!selectedLead.description && selectedLead.message && (
                  <div className="col-span-2"><span className="text-gray-500">Nachricht:</span> {selectedLead.message}</div>
                )}
                {selectedLead.floor && <div><span className="text-gray-500">Etage:</span> {selectedLead.floor}</div>}
                {selectedLead.timeline && <div><span className="text-gray-500">Zeitraum:</span> {selectedLead.timeline}</div>}
                {selectedLead.site_access && selectedLead.site_access.length > 0 && (
                  <div className="col-span-2"><span className="text-gray-500">Zugang:</span> {selectedLead.site_access.join(', ')}</div>
                )}
                {selectedLead.utm_campaign && <div className="col-span-2"><span className="text-gray-500">Kampagne:</span> {selectedLead.utm_campaign} {selectedLead.utm_source ? `(${selectedLead.utm_source})` : ''}</div>}
              </div>

              {/* Photos */}
              {selectedLead.photos && selectedLead.photos.length > 0 && (
                <div className="mt-3">
                  <p className="text-gray-500 text-xs mb-2">Fotos ({selectedLead.photos.length})</p>
                  <div className="grid grid-cols-4 gap-2">
                    {selectedLead.photos.map((photo) => (
                      <a key={photo.id} href={`${API_URL}/api/files/${photo.id}`} target="_blank" rel="noopener noreferrer" className="aspect-square rounded-lg overflow-hidden border hover:border-[#D4AA6A] transition-colors">
                        <img src={`${API_URL}/api/files/${photo.id}`} alt={photo.filename} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 mt-3">
                {(() => {
                  const wa = isValidWhatsAppNumber(normalizePhoneForWhatsApp(selectedLead.phone));
                  return (
                    <Button
                      size="sm"
                      variant="outline"
                      className={`${wa ? 'text-green-600 border-green-200' : 'text-gray-400 border-gray-200 cursor-not-allowed'}`}
                      disabled={!wa}
                      title={wa ? 'WhatsApp öffnen' : 'Keine gültige WhatsApp-Nummer vorhanden'}
                      onClick={() => openWhatsApp(selectedLead.phone)}
                      data-testid="lead-detail-wa"
                    >
                      <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                    </Button>
                  );
                })()}
                <Button size="sm" variant="outline" className="text-blue-600 border-blue-200" onClick={() => window.open(`tel:${selectedLead.phone}`, '_self')}>
                  <Phone className="w-3 h-3 mr-1" /> Anrufen
                </Button>
                {selectedLead.email && (
                  <Button size="sm" variant="outline" className="text-purple-600 border-purple-200" onClick={() => window.open(`mailto:${selectedLead.email}`, '_self')}>
                    <Mail className="w-3 h-3 mr-1" /> E-Mail
                  </Button>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="p-5">
              <h4 className="font-semibold text-sm text-[#4A331D] mb-3">Notizen</h4>
              <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                {(!selectedLead.notes || selectedLead.notes.length === 0) ? (
                  <p className="text-sm text-gray-400">Keine Notizen vorhanden</p>
                ) : (
                  selectedLead.notes.map((note) => (
                    <div key={note.timestamp || `${note.text}-${note.author}`} className="bg-gray-50 rounded p-2 text-sm">
                      <p className="text-gray-700">{note.text}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {note.author} • {new Date(note.timestamp).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <Textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Notiz hinzufügen..."
                  rows={2}
                  className="text-sm"
                  data-testid="note-input"
                />
                <Button
                  onClick={addNote}
                  disabled={addingNote || !noteText.trim()}
                  className="bg-[#4A331D] hover:bg-[#593B23] self-end"
                  size="sm"
                  data-testid="add-note-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="p-4 border-t">
              <Button variant="outline" onClick={() => setSelectedLead(null)} className="w-full" size="sm">
                Schließen
              </Button>
            </div>

            {/* === Attribution Phase 1 === */}
            <div className="p-5 border-t bg-gradient-to-b from-amber-50/40 to-white" data-testid="lead-attribution">
              <h4 className="font-semibold text-sm text-[#4A331D] mb-3 flex items-center gap-2">
                Attribution &amp; Tracking
                {selectedLead.source_classification && (
                  <span className="px-2 py-0.5 rounded-full bg-[#4A331D] text-white text-[10px] uppercase tracking-wide">
                    {selectedLead.source_classification}
                  </span>
                )}
                {selectedLead.is_returning_lead && (
                  <span
                    className="px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] uppercase tracking-wide"
                    title={`Frühere Lead-IDs: ${(selectedLead.previous_lead_ids || []).join(', ')}`}
                    data-testid="returning-lead-badge"
                  >
                    Wiederkehrend
                  </span>
                )}
              </h4>
              {(() => {
                const a = selectedLead.attribution || {};
                const Row = ({ label, value }) => (value !== undefined && value !== null && value !== '') ? (
                  <div className="flex gap-2 text-xs py-0.5">
                    <span className="text-gray-500 min-w-[140px]">{label}:</span>
                    <span className="text-gray-800 break-all">{String(value)}</span>
                  </div>
                ) : null;
                const ttc = selectedLead.time_to_conversion_seconds;
                const ttcStr = ttc != null
                  ? (ttc < 60 ? `${ttc} s` : ttc < 3600 ? `${Math.round(ttc / 60)} min` : ttc < 86400 ? `${(ttc / 3600).toFixed(1)} h` : `${Math.round(ttc / 86400)} Tage`)
                  : null;
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0">
                    <Row label="Visitor ID" value={a.visitor_id} />
                    <Row label="Session ID" value={a.session_id} />
                    <Row label="Landing Page" value={a.initial_landing_page || a.landing_page} />
                    <Row label="Referrer" value={a.initial_referrer || a.referrer} />
                    <Row label="utm_source" value={a.utm_source || a.initial_utm_source} />
                    <Row label="utm_medium" value={a.utm_medium || a.initial_utm_medium} />
                    <Row label="utm_campaign" value={a.utm_campaign || a.initial_utm_campaign} />
                    <Row label="utm_term" value={a.utm_term || a.initial_utm_term} />
                    <Row label="utm_content" value={a.utm_content || a.initial_utm_content} />
                    <Row label="gclid" value={a.gclid} />
                    <Row label="wbraid" value={a.wbraid} />
                    <Row label="gbraid" value={a.gbraid} />
                    <Row label="fbclid" value={a.fbclid} />
                    <Row label="gad_campaignid" value={a.gad_campaignid} />
                    <Row label="gad_adgroupid" value={a.gad_adgroupid} />
                    <Row label="gad_keyword" value={a.gad_keyword} />
                    <Row label="Gerät" value={a.device_type} />
                    <Row label="Browser" value={a.browser} />
                    <Row label="OS" value={a.os} />
                    <Row label="Page Views" value={a.page_view_count} />
                    <Row label="1. Besuch" value={a.first_visit ? new Date(a.first_visit).toLocaleString('de-DE') : null} />
                    <Row label="Zeit bis Conversion" value={ttcStr} />
                  </div>
                );
              })()}
            </div>

            {/* === CRM Lifecycle === */}
            <div className="p-5 border-t" data-testid="lead-lifecycle">
              <h4 className="font-semibold text-sm text-[#4A331D] mb-3">CRM-Lifecycle</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500">Qualität</label>
                  <Select value={lifecycle.quality || undefined} onValueChange={(v) => setLifecycle(s => ({ ...s, quality: v }))}>
                    <SelectTrigger className="mt-1" data-testid="lifecycle-quality">
                      <SelectValue placeholder="A / B / C / D" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">A — heißer Lead</SelectItem>
                      <SelectItem value="B">B — gut</SelectItem>
                      <SelectItem value="C">C — neutral</SelectItem>
                      <SelectItem value="D">D — gering</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Abschluss am</label>
                  <Input type="date" value={lifecycle.closed_at}
                    onChange={(e) => setLifecycle(s => ({ ...s, closed_at: e.target.value }))}
                    className="mt-1" data-testid="lifecycle-closed-at" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Auftragswert (€)</label>
                  <Input type="number" step="0.01" min="0" value={lifecycle.order_value}
                    onChange={(e) => setLifecycle(s => ({ ...s, order_value: e.target.value }))}
                    className="mt-1" data-testid="lifecycle-order-value" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Gewinn (€)</label>
                  <Input type="number" step="0.01" value={lifecycle.profit}
                    onChange={(e) => setLifecycle(s => ({ ...s, profit: e.target.value }))}
                    className="mt-1" data-testid="lifecycle-profit" />
                </div>
              </div>
              <Button size="sm" className="mt-3 bg-[#4A331D] hover:bg-[#593B23]"
                onClick={saveLifecycle} disabled={savingLifecycle} data-testid="lifecycle-save">
                {savingLifecycle ? 'Speichere…' : 'CRM-Daten speichern'}
              </Button>
            </div>

            {/* === Event-Timeline === */}
            <div className="p-5 border-t" data-testid="lead-event-timeline">
              <h4 className="font-semibold text-sm text-[#4A331D] mb-3">
                Event-Timeline ({leadEvents.length})
              </h4>
              {leadEvents.length === 0 ? (
                <p className="text-xs text-gray-400">Keine Tracking-Events verknüpft.</p>
              ) : (
                <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {leadEvents.map((ev) => (
                    <li key={ev.id} className="text-xs border-l-2 border-[#D4AA6A] pl-3 py-1">
                      <div className="font-medium text-[#4A331D]">{ev.event_name} <span className="text-gray-400 font-normal">({ev.event_type || '—'})</span></div>
                      <div className="text-gray-500">{new Date(ev.created_at).toLocaleString('de-DE')}</div>
                      {ev.page_url && <div className="text-gray-600 break-all">{ev.page_url}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Single-Delete Confirm */}
      <AlertDialog open={!!deleteTargetId} onOpenChange={(o) => !o && setDeleteTargetId(null)}>
        <AlertDialogContent data-testid="single-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Lead wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Dieser Lead wird unwiderruflich aus der Datenbank entfernt. Die Aktion wird im Audit-Log protokolliert.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="single-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmSingleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="single-delete-confirm"
            >
              {deleting ? 'Lösche…' : 'Löschen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk-Delete Confirm */}
      <AlertDialog open={bulkConfirmOpen} onOpenChange={(o) => !deleting && setBulkConfirmOpen(o)}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Möchtest du wirklich {selectedIds.size} Leads dauerhaft löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Leads werden unwiderruflich aus der Datenbank entfernt. Die Aktion wird im Audit-Log protokolliert.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="bulk-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="bulk-delete-confirm"
            >
              {deleting ? 'Lösche…' : 'Löschen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminDashboard;
